/** Latched at logout: `window.location.assign` navigates without stopping
 *  script execution, so a pending response can still reach `write()` after the
 *  session was wiped — and that write would outlive the session. */
let writesSuspended = false;

export function suspendFilterWrites(): void {
  writesSuspended = true;
}

/** Only specs need this — they share one document, where a real logout gets a
 *  fresh module on the next page. */
export function resumeFilterWrites(): void {
  writesSuspended = false;
}

/**
 * Session-scoped memory for one list screen's filters.
 *
 * Every portal's lists keep their filters across a round trip to a detail page
 * or a form, so returning puts the user back where they left. `sessionStorage`
 * rather than `localStorage`: a filter set is a working state, not a
 * preference — a new tab should start clean.
 *
 * Keys follow `<showId>.<portal>.<screen>.filters`, which keeps two shows (or
 * two portals on the same show) from reading each other's state.
 */
export class FilterStorage<T extends object> {
  constructor(private readonly key: string) {}

  /** Stored values, or `undefined` when nothing was saved or the entry is junk. */
  read(): Partial<T> | undefined {
    const raw = sessionStorage.getItem(this.key);
    if (!raw) {
      return undefined;
    }

    try {
      return JSON.parse(raw) as Partial<T>;
    } catch {
      // A malformed entry just means the defaults apply.
      return undefined;
    }
  }

  write(value: T): void {
    if (writesSuspended) {
      return;
    }
    sessionStorage.setItem(this.key, JSON.stringify(value));
  }

  clear(): void {
    sessionStorage.removeItem(this.key);
  }
}

/** `<showId>.<portal>.<screen>.filters` — the key shape every screen uses. */
export function filterStorageKey(showId: string, portal: string, screen: string): string {
  return `${showId}.${portal}.${screen}.filters`;
}

/**
 * What a screen built on `app-entries-list` keeps. `values` is keyed by
 * `EntriesListFilter.key`, so a screen that gains or drops a dropdown needs no
 * change here — and an old entry missing the new key just falls back to "All".
 */
export interface StoredListFilters {
  values: Record<string, string>;
  searchText?: string;
  page?: number;
  pageSize?: number;
}
